import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/axios";
import Select from "react-select";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingOverlay from "../../components/LoadingOverlay";

import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";

const CreateRequest = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [products, setProducts] = useState([]);
  const [details, setDetails] = useState([]);
  const [observations, setObservations] = useState("");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(isEdit);

  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    productId: "",
    presentationId: "",
    stock: "",
    quantity: "",
  });

  const [presentations, setPresentations] = useState([]);

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const presentationOptions = presentations.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  useEffect(() => {
    fetchProducts();
    fetchPresentations();
    if (isEdit) fetchRequest();
  }, [isEdit, id]);

  const fetchRequest = async () => {
    try {
      setLoadingRequest(true);
      const res = await api.get(`/inventory-requests/${id}`);
      setObservations(res.data.observations || "");
      setDetails(
        (res.data.request_details || []).map((detail) => ({
          productId: detail.product_id,
          productName: detail.product?.name || "",
          presentationId: detail.presentation_id,
          presentationName: detail.presentation?.name || "",
          stock: Number(detail.stock_at_request || 0),
          quantity: Number(detail.requested_quantity),
        })),
      );
    } catch (error) {
      toast.error(error.response?.data?.message || "No fue posible cargar la solicitud.");
      navigate("/requests");
    } finally {
      setLoadingRequest(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data);
    } catch (error) {
      console.error(error);
      toast.error("No fue posible cargar los productos.");
    }
  };

  const fetchPresentations = async () => {
    try {
      const res = await api.get("/presentations");
      setPresentations(res.data);
    } catch (error) {
      console.error(error);
      toast.error("No fue posible cargar las presentaciones.");
    }
  };

  // =====================================================
  // CAMBIO DE PRODUCTO
  // Consulta el stock real del área
  // =====================================================
  const handleProductChange = async (selected) => {
    if (!selected) {
      setForm({
        productId: "",
        presentationId: "",
        stock: "",
        quantity: "",
      });

      return;
    }

    try {
      const res = await api.get(`/client-inventory/product/${selected.value}`);

      setForm({
        productId: selected.value,
        presentationId: "",
        stock: Number(res.data.quantity ?? 0),
        quantity: "",
      });
    } catch (error) {
      console.error(error);

      toast.error("No fue posible consultar el stock actual del área.");

      // Si no existe inventario, asumimos 0
      setForm({
        productId: selected.value,
        presentationId: "",
        stock: 0,
        quantity: "",
      });
    }
  };

  // =====================================================
  // AGREGAR PRODUCTO
  // =====================================================
  const handleAddDetail = () => {
    const newErrors = {};

    if (!form.productId) {
      newErrors.productId = "Seleccione un producto";
    }

    if (!form.presentationId) {
      newErrors.presentationId = "Seleccione una presentación";
    }

    if (!form.quantity || parseInt(form.quantity) <= 0) {
      newErrors.quantity = "Ingrese una cantidad válida";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const product = products.find((p) => p.id === parseInt(form.productId));

    if (!product) {
      toast.error("Producto no encontrado.");
      return;
    }

    const existing = details.findIndex(
      (d) =>
        d.productId === parseInt(form.productId) &&
        d.presentationId === parseInt(form.presentationId),
    );

    if (existing !== -1) {
      const updated = [...details];

      updated[existing].quantity += parseInt(form.quantity);

      setDetails(updated);
    } else {
      setDetails([
        ...details,
        {
          productId: product.id,
          productName: product.name,
          presentationId: parseInt(form.presentationId),
          presentationName: presentations.find(
            (p) => p.id === parseInt(form.presentationId),
          )?.name,

          // Stock consultado directamente del inventario
          stock: Number(form.stock || 0),

          quantity: parseInt(form.quantity),
        },
      ]);
    }

    // Limpiar formulario
    setForm({
      productId: "",
      presentationId: "",
      stock: "",
      quantity: "",
    });

    setErrors({});
  };

  // =====================================================
  // ELIMINAR PRODUCTO
  // =====================================================
  const handleRemoveDetail = (index) => {
    const updated = details.filter((_, i) => i !== index);

    setDetails(updated);
  };

  // =====================================================
  // DISMINUIR CANTIDAD
  // =====================================================
  const decreaseQuantity = (index) => {
    const updated = [...details];

    if (updated[index].quantity <= 1) {
      handleRemoveDetail(index);
      return;
    }

    updated[index].quantity--;

    setDetails(updated);
  };

  // =====================================================
  // AUMENTAR CANTIDAD
  // =====================================================
  const increaseQuantity = (index) => {
    const updated = [...details];

    updated[index].quantity++;

    setDetails(updated);
  };

  // =====================================================
  // PREPARAR ENVÍO
  // =====================================================
  const handlePreSubmit = () => {
    const newErrors = {};

    if (details.length === 0) {
      newErrors.details = "Debe agregar al menos un producto.";

      toast.error("Debe agregar productos a la solicitud.");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setShowConfirmModal(true);
  };

  // =====================================================
  // ENVIAR SOLICITUD
  // =====================================================
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const payload = {
        observations,

        products: details.map((item) => ({
          product_id: item.productId,
          presentation_id: item.presentationId,
          requested_quantity: item.quantity,
          observations: null,
        })),
      };

      if (isEdit) {
        await api.put(`/inventory-requests/${id}`, payload);
      } else {
        await api.post("/inventory-requests", payload);
      }

      Swal.fire({
        icon: "success",
        title: isEdit ? "Solicitud actualizada" : "Solicitud creada",
        text: isEdit
          ? "La solicitud fue actualizada correctamente."
          : "La solicitud fue enviada correctamente.",
        timer: 2000,
        showConfirmButton: false,
      }).then(() => navigate("/requests"));
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.message || "Error al crear la solicitud.",
      );

      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative bg-white p-6 rounded-xl shadow-md max-w-7xl mx-auto">
      <LoadingOverlay loading={loadingRequest} />
      <div className="justify-end flex mb-4">
        <button
          onClick={() => navigate("/requests")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} />
          Volver a Solicitudes
        </button>
      </div>
      <h2 className="text-2xl font-bold text-center mb-6">
        {isEdit ? "Editar Solicitud de Insumos" : "Nueva Solicitud de Insumos"}
      </h2>

      <div className="mb-6">
        <label className="font-semibold">Observaciones</label>

        <textarea
          rows={3}
          value={observations}
          onChange={(e) => setObservations(e.target.value)}
          placeholder="Observaciones generales (Opcional)"
          className="w-full mt-1 border rounded px-3 py-2"
        />
      </div>

      <hr className="my-6" />

      <h3 className="text-xl font-semibold text-center mb-5">
        Agregar Productos
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
        {/* Producto */}
        <div className="lg:col-span-3">
          <label className="block text-sm font-medium mb-1">Producto</label>

          <Select
            options={productOptions}
            placeholder="Buscar producto..."
            isClearable
            value={
              productOptions.find((p) => p.value === form.productId) || null
            }
            onChange={handleProductChange}
          />

          {errors.productId && (
            <p className="text-red-600 text-sm mt-1">{errors.productId}</p>
          )}
        </div>

        {/* Presentación */}
        <div className="lg:col-span-3">
          <label className="block text-sm font-medium mb-1">Presentación</label>

          <Select
            options={presentationOptions}
            placeholder="Buscar presentación..."
            isClearable
            value={
              presentationOptions.find(
                (p) => p.value === form.presentationId,
              ) || null
            }
            onChange={(selected) =>
              setForm({
                ...form,
                presentationId: selected?.value || "",
              })
            }
          />

          {errors.presentationId && (
            <p className="text-red-600 text-sm mt-1">{errors.presentationId}</p>
          )}
        </div>

        {/* Stock */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium mb-1">
            Stock actual del área
          </label>

          <input
            type="number"
            value={form.stock}
            readOnly
            className="w-full border px-4 py-2 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
            placeholder="Seleccione un producto"
          />
        </div>

        {/* Cantidad */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium mb-1">
            Cantidad solicitada
          </label>

          <input
            type="number"
            min="1"
            value={form.quantity}
            onChange={(e) =>
              setForm({
                ...form,
                quantity: e.target.value,
              })
            }
            className={`w-full border px-4 py-2 rounded-lg ${
              errors.quantity ? "border-red-500" : "border-gray-300"
            }`}
          />

          {errors.quantity && (
            <p className="text-red-600 text-sm mt-1">{errors.quantity}</p>
          )}
        </div>

        {/* Botón */}
        <div className="lg:col-span-2 flex items-end">
          <button
            onClick={handleAddDetail}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center justify-center gap-2 shadow"
          >
            <Plus size={18} />
            Agregar Producto
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl shadow mt-6">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 uppercase text-sm text-center">
              <th className="px-6 py-3">Producto</th>
              <th className="px-6 py-3">Presentación</th>
              <th className="px-6 py-3">Stock Actual</th>
              <th className="px-6 py-3">Cantidad</th>
              <th className="px-6 py-3 w-28">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {details.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-gray-400 italic">
                  No hay productos agregados.
                </td>
              </tr>
            ) : (
              details.map((item, index) => (
                <tr
                  key={index}
                  className={`border-t ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-6 py-4 font-medium">{item.productName}</td>

                  <td className="px-6 py-4">{item.presentationName}</td>

                  <td className="px-6 py-4 font-semibold text-gray-700">
                    {item.stock}
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => decreaseQuantity(index)}
                        className="w-8 h-8 rounded bg-red-500 hover:bg-red-600 text-white font-bold"
                      >
                        -
                      </button>

                      <span className="w-8 text-center font-bold text-blue-600">
                        {item.quantity}
                      </span>

                      <button
                        onClick={() => increaseQuantity(index)}
                        className="w-8 h-8 rounded bg-green-500 hover:bg-green-600 text-white font-bold"
                      >
                        +
                      </button>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleRemoveDetail(index)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-600 hover:text-white transition"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {errors.details && (
        <p className="text-red-500 text-sm mt-3">{errors.details}</p>
      )}

      <div className="flex justify-end mt-6">
        <button
          onClick={handlePreSubmit}
          disabled={isSubmitting}
          className={`px-6 py-2 rounded-lg flex items-center gap-2 text-white ${
            isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          <Save size={18} />

          {isSubmitting
            ? isEdit
              ? "Actualizando..."
              : "Enviando..."
            : isEdit
              ? "Guardar Cambios"
              : "Enviar Solicitud"}
        </button>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
            <h3 className="text-xl font-bold text-center">
              Confirmar Solicitud
            </h3>

            <p className="text-gray-500 text-center mt-3">
              Se enviará una solicitud con
              <strong> {details.length} producto(s)</strong>.
            </p>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                Cancelar
              </button>

              <button
                onClick={handleSubmit}
                className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateRequest;
